import { useEffect, useState, useCallback } from "react";
import ClientService from "../../rest/clientService";
import { toast } from "react-toastify";
import TopNavBar from "../../components/reusables/topNavBar";
import { useAdminPreferencesUpdates } from "../../ws/useAdminPreferencesUpdates";
import PreferencesTab from "./components/preferencesTab";
import ConfigurationsTab from "./components/configurationsTab";
import CentralModal from "../../components/lib/centralModal";
import CustomButton from "../../components/lib/customButton";
import { IoWarningOutline } from "react-icons/io5";

function PreferencesPage() {
    const [clientPreferences, setClientPreferences] = useState(null);
    const [originalPreferences, setOriginalPreferences] = useState(null);
    const [turns, setTurns] = useState([]);
    const [originalTurns, setOriginalTurns] = useState([]);
    const [activeTab, setActiveTab] = useState('preferences');
    const [isSaving, setIsSaving] = useState(false);
    
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);

    const normalizeTurnFromServer = (raw) => {
        const turnId = raw.turnId ?? raw.turn_id ?? raw.id ?? null;
        return {
            turn_id: turnId,
            name: raw.name ?? raw.turn_name ?? "",
            startTime: raw.startTime ?? raw.start_time ?? raw.start_time_str ?? "",
            endTime: raw.endTime ?? raw.end_time ?? raw.end_time_str ?? "",
            isActive: (raw.isActive ?? raw.is_active ?? raw.enabled ?? false) ? true : false,
            clientCode: raw.clientCode ?? raw.client_code ?? raw.client ?? null,
            __raw: raw
        };
    };

    const mapTurnToServerFormat = (t) => {
        const isTemp = typeof t.turn_id === 'string' && t.turn_id.startsWith('tmp-');
        return {
            turnId: isTemp ? null : t.turn_id,
            clientCode: t.clientCode,
            name: t.name,
            startTime: t.startTime,
            endTime: t.endTime,
            isActive: !!t.isActive
        };
    };

    const loadData = useCallback(async () => {
        try {
            const clientData = await ClientService.getClientAdminPreferences();
            const turnsDataFromServer = await ClientService.getTurns();

            setClientPreferences(clientData);
            setOriginalPreferences(JSON.parse(JSON.stringify(clientData)));

            const normalized = (turnsDataFromServer || []).map(normalizeTurnFromServer);
            setTurns(normalized);
            setOriginalTurns(JSON.parse(JSON.stringify(normalized)));
        } catch (error) {
            console.error(error);
            toast.error("Erro ao carregar preferências.");
        }
    }, []);

    const clientCode = sessionStorage.getItem('clientCode');
    useAdminPreferencesUpdates({ fetchPreferences: loadData, clientCode });

    useEffect(() => { loadData(); }, [loadData]);

    const hasChanges = () => {
        if (!clientPreferences || !originalPreferences) return false;
        const prefChanged = JSON.stringify(clientPreferences) !== JSON.stringify(originalPreferences);
        const turnsChanged = JSON.stringify(turns) !== JSON.stringify(originalTurns);
        return prefChanged || turnsChanged;
    };

    const handleResetClick = () => {
        setIsResetModalOpen(true);
    };

    const confirmReset = () => {
        setClientPreferences(JSON.parse(JSON.stringify(originalPreferences)));
        setTurns(JSON.parse(JSON.stringify(originalTurns)));
        setIsResetModalOpen(false);
        toast.info("Alterações descartadas.");
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            await ClientService.updateClientAdminPreferences(clientPreferences);
            const payload = turns.map(mapTurnToServerFormat);
            await ClientService.updateTurns(payload);
            
            setOriginalPreferences(JSON.parse(JSON.stringify(clientPreferences)));
            setOriginalTurns(JSON.parse(JSON.stringify(turns))); 
            
            toast.success("Alterações salvas com sucesso!");
            loadData();
        } catch (error) {
            console.error("Erro ao salvar:", error);
            toast.error("Erro ao salvar!");
        } finally {
            setIsSaving(false);
        }
    };

    return (
    <div className='h-full flex flex-col'>
            <TopNavBar title={"Preferências"} subtitle={""} />

            {clientPreferences ? (
                <div className="flex flex-col gap-6 mx-4 md:mx-16 my-6">
                    <div className="flex border-b mb-4 sticky top-0 bg-gray-50 z-20 pt-2">
                        <button
                            className={`px-4 py-2 font-semibold transition-colors ${activeTab === 'preferences' ? 'border-b-2 border-custom_blue text-custom_blue' : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setActiveTab('preferences')}
                        >
                            Preferências Gerais
                        </button>
                        <button
                            className={`px-4 py-2 font-semibold transition-colors ${activeTab === 'configurations' ? 'border-b-2 border-custom_blue text-custom_blue' : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setActiveTab('configurations')}
                        >
                            Configurações Avançadas
                        </button>
                    </div>

                    <div className="min-h-[50vh]">
                        {activeTab === 'preferences' && (
                            <PreferencesTab 
                                clientPreferences={clientPreferences}
                                setClientPreferences={setClientPreferences}
                            />
                        )}

                        {activeTab === 'configurations' && (
                            <ConfigurationsTab 
                                clientPreferences={clientPreferences}
                                setClientPreferences={setClientPreferences}
                                turns={turns}
                                setTurns={setTurns}
                            />
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-center h-64">
                    <p className="text-custom_gray_font animate-pulse">Carregando configurações...</p>
                </div>
            )}

            <div className={`fixed bottom-0 right-0 left-0 md:left-[100px] z-40 transition-transform duration-300 ease-in-out ${hasChanges() ? 'translate-y-0' : 'translate-y-full'}`}>
                <div className="bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] p-4 px-8 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-amber-600">
                        <IoWarningOutline className="text-xl" />
                        <span className="font-medium text-sm md:text-base">Você tem alterações não salvas.</span>
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleResetClick}
                            disabled={isSaving}
                            className="text-gray-500 hover:text-gray-800 font-medium px-4 py-2 rounded-xl hover:bg-gray-100 transition disabled:opacity-50"
                        >
                            Descartar
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className={`px-6 py-2.5 rounded-xl font-bold text-white shadow-lg shadow-custom_green/30 transform active:scale-95 transition-all
                                ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-custom_green hover:bg-green-600'}`}
                        >
                            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                        </button>
                    </div>
                </div>
            </div>

            <CentralModal
                isOpen={isResetModalOpen}
                onClose={() => setIsResetModalOpen(false)}
                title="Descartar Alterações?"
                width="max-w-md"
            >
                <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3 bg-amber-50 p-4 rounded-lg border border-amber-100">
                        <IoWarningOutline className="text-amber-600 text-xl mt-1 shrink-0" />
                        <p className="text-gray-700">
                            Tem certeza que deseja descartar todas as alterações não salvas? 
                            <br/>
                            <span className="text-sm text-gray-500 mt-1 block">Esta ação reverterá as configurações para o último estado salvo.</span>
                        </p>
                    </div>
                    
                    <div className="flex justify-end gap-3 mt-4">
                        <CustomButton
                            text="Cancelar"
                            type="button"
                            buttonType="neutral"
                            showIcon={false}
                            onClick={() => setIsResetModalOpen(false)}
                            className="w-full md:w-auto"
                        />
                        <CustomButton
                            text="Sim, descartar"
                            type="button"
                            buttonType="delete"
                            showIcon={false}
                            onClick={confirmReset}
                            className="w-full md:w-auto"
                        />
                    </div>
                </div>
            </CentralModal>
        </div>
    );
}

export default PreferencesPage;